import { Container, Divider, Typography } from '@cleartrip/ct-design-components'
import React, { Fragment } from 'react'

import { FilterData, PossibleFilterTypes, FilterData as selectedFiltersType } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import {
  filterTypes,
  SRP_FILTER_ORDER_CONTROL,
  SRP_REVAMP_FILTER_ORDER,
  SRP_REVAMP_FILTER_RAIL_WIDTH,
  SRP_REVAMP_FILTER_STICKY_TOP,
  SrpFilterSection,
} from '@/constants/Srp'
import StickyBox from 'react-sticky-box'
import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { useAppSelector } from '@/redux/store/hooks'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { ProcessedData } from '@/utils/srp/types'

import { getSearchIntentDetails, isPerAdultPricing } from '@/utils/srp/helper'
import CtUpgradeFilter from './ctUpgradeFilter'
import VisaRejectionFilter from './visaRejectionFilter'
import DurationFilterNew from './DurationFilterNew'
import TotalRoundTripPrice from './totalRoundTripPrice'

import NearbyAirportNew from './NearbyAirportNew'
import StopsFilterNew from './StopsFilterNew'
import TimingsFilterNew from './TimingsFilterNew'
import PriceFilterNew from './PriceFilterNew'
import AirlineFilterNew from './AirlineFilterNew'
import CheckInBaggageFilterNew from './CheckInBaggageFilterNew'
import LayoverAirportFilterNew from './LayoverAirportFilterNew'
import AppliedFiltersAllChips from './AppliedFiltersAllChips'
import { withErrorBoundary } from '@/components/shared/SentryErrorBoundary'
import FilterErrorFallback from './FilterErrorFallback'
import PtcFareFilterContainer from './PtcFareFilter/PtcFareFilterContainer'

type FiltersProps = {
  selectedFilters: selectedFiltersType
  updateFilterData: (selectedFilters: any) => void
  isRT: boolean
  totalFlightsCount: number
  filteredFlightsCount: number
  resetFilters: () => void
  clearFiltersCategoryWise: (filterData: FilterData, isRT: boolean, filterType: string) => void
  processedData: Partial<ProcessedData>
  setSelectedPrice: any
  isIntl?: boolean
  updatedReturnListLength: number
  onewayFilteredFlightsCount: number
  masterTuppleRef: any
  resetDurationFilters: (filterData: FilterData) => void
  resetTimingsFilters: (filterData: FilterData) => void
  isNonStopDefaultApplied?: boolean
  onNonStopTooltipDismiss?: () => void
  isSrpRevamp?: boolean
}

const DEFAULT_TUPPLE_HEIGHT = 110
const INTL_RT_EXTRA_PADDING = 180

const FilterSection = ({
  isRevamp,
  showDivider = true,
  children,
}: {
  isRevamp: boolean
  showDivider?: boolean
  children: React.ReactNode
}) =>
  isRevamp ? (
    <Container className='flex flex-column flex-rg-3 ba-solid bc-line-500 bg-neutral-100 br-16 p-3'>
      {children}
    </Container>
  ) : (
    <>
      {showDivider && <Divider borderColor='#E6E6E6' className='w-100p my-2' />}
      {children}
    </>
  )

const Filters: React.FC<FiltersProps> = ({
  selectedFilters,
  updateFilterData,
  isRT,
  totalFlightsCount,
  filteredFlightsCount,
  resetFilters,
  clearFiltersCategoryWise,
  resetDurationFilters,
  resetTimingsFilters,
  processedData = {},
  setSelectedPrice,
  isIntl = false,
  updatedReturnListLength,
  onewayFilteredFlightsCount,
  masterTuppleRef,
  isNonStopDefaultApplied = false,
  onNonStopTooltipDismiss,
  isSrpRevamp = false,
}) => {
  const {
    stops,
    airlineFilter,
    departureTimeFrom,
    nearbyAirportFilter,
    tripDuration,
    layoverDuration,
    oneWayPrice,
    fareInclusions,
    arrivalTimeFrom,
    layoverAirport,
    ctUpgrade: onwardCtUpgrade,
    visaRejection: onwardVisaRejection,
    ptcFare: onwardPtcFare,
  } = selectedFilters?.onwardLeg || {}
  const {
    stops: returnStops,
    airlineFilter: returnAirlineFilter,
    departureTimeFrom: returnDepartureTimeFrom,
    nearbyAirportFilter: returnNearbyAirportFilter,
    tripDuration: returnTripDuration,
    layoverDuration: returnLayoverDuration,
    oneWayPrice: returnOneWayPrice,
    arrivalTimeFrom: returnArrivalTimeFrom,
    fareInclusions: returnFareInclusions,
    layoverAirport: returnLayoverAirport,
    ctUpgrade: returnCtUpgrade,
    visaRejection: returnVisaRejection,
    ptcFare: returnPtcFare,
  } = selectedFilters?.returnLeg || {}
  const { totalRoundTripPrice } = selectedFilters || {}
  const showCtUpgrade = onwardCtUpgrade || returnCtUpgrade
  const showVisaRejection = onwardVisaRejection || returnVisaRejection
  const updatedCardCount = isRT && isIntl ? updatedReturnListLength + onewayFilteredFlightsCount : filteredFlightsCount
  // RT needs layovers on both legs, mirroring how LayoverAirportFilterNew renders the legs.
  const showLayoverAirport = isRT
    ? !isEmpty(layoverAirport?.availableLayoverAirport) && !isEmpty(returnLayoverAirport?.availableLayoverAirport)
    : !isEmpty(layoverAirport?.availableLayoverAirport)
  const checkInBaggageRequired = (fareInclusions: any) => {
    let showBaggageFilter = false
    if (fareInclusions?.options?.length > 0) {
      fareInclusions?.options.forEach((option: any) => {
        if (option.value == 'withBaggage') {
          showBaggageFilter = true
        }
      })
    }

    return showBaggageFilter
  }

  const showResetFilters = filteredFlightsCount < totalFlightsCount
  const { searchData } = useAppSelector(getSrpSearchData)
  const { searchIntent } = searchData
  const { paxCriteria = [] } = getSearchIntentDetails(searchIntent)
  const showPerAdultPricing = isPerAdultPricing(paxCriteria)
  const basePadding = masterTuppleRef?.current?.clientHeight || DEFAULT_TUPPLE_HEIGHT
  const additionalPadding = isRT && isIntl && !isSrpRevamp ? INTL_RT_EXTRA_PADDING : 0
  const filterBottomPadding = basePadding + additionalPadding
  const onFilterChange = (
    type: PossibleFilterTypes,
    selectedValues: any,
    index = 0,
    legKey: 'onwardLeg' | 'returnLeg' = 'onwardLeg'
  ) => {
    const {
      STOPS,
      DEPARTURE_TIME_FROM,
      ARRIVAL_TIME_FROM,
      AIRLINE_FILTER,
      NEARBY_AIRPORT,
      TRIP_DURATION,
      LAYOVER_DURATION,
      ONE_WAY_PRICE,
      CHECK_IN_BAGGAGE,
      CT_UPGRADE,
      LAYOVER_AIRPORT,
      TOTAL_ROUND_TRIP_PRICE,
      VISA_REJECTION,
      PTC_FARE,
    } = filterTypes
    sendRavenOnFilterChange(
      type,
      type === AIRLINE_FILTER ? selectedValues?.selectedValues : selectedValues,
      index,
      legKey
    )
    const currentField = showPerAdultPricing ? 'currentADT' : 'current'
    switch (type) {
      case VISA_REJECTION:
        updateFilterData((prevState: any) => {
          return {
            ...prevState,
            [type]: selectedValues,
          }
        })
        return
      case PTC_FARE:
        updateFilterData((prevState: any) => {
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: selectedValues,
            },
          }
        })
        return
      case CT_UPGRADE:
        updateFilterData((prevState: any) => {
          return {
            ...prevState,
            [type]: selectedValues,
          }
        })
        return
      case STOPS:
        updateFilterData((prevState: any) => {
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: {
                ...prevState[legKey]?.[type],
                selectedValues: selectedValues,
              },
            },
          }
        })
        return
      case AIRLINE_FILTER:
        updateFilterData((prevState: any) => {
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: {
                ...prevState[legKey]?.[type],
                selectedValues: selectedValues?.selectedValues,
                showMultiAirlineItineraries: selectedValues?.showMultiAirlineItineraries,
              },
            },
          }
        })
        return
      case DEPARTURE_TIME_FROM:
        updateFilterData((prevState: any) => {
          const currentDepartureTimeFrom = prevState[legKey]?.departureTimeFrom || []
          const updatedDepartureTimeFrom = [...currentDepartureTimeFrom]
          updatedDepartureTimeFrom[index] = {
            ...updatedDepartureTimeFrom[index],
            selectedValues: selectedValues,
          }
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: updatedDepartureTimeFrom,
            },
          }
        })
        return
      case ARRIVAL_TIME_FROM:
        updateFilterData((prevState: any) => {
          const currentArrivalTimeFrom = prevState[legKey]?.arrivalTimeFrom || []
          const updatedArrivalTimeFrom = [...currentArrivalTimeFrom]
          updatedArrivalTimeFrom[index] = {
            ...updatedArrivalTimeFrom[index],
            selectedValues: selectedValues,
          }
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: updatedArrivalTimeFrom,
            },
          }
        })
        return
      case NEARBY_AIRPORT:
        updateFilterData((prevState: any) => {
          const currentNearbyAirport = prevState[legKey]?.nearbyAirportFilter || []
          const updatedNearbyAirport = [...currentNearbyAirport]
          updatedNearbyAirport[index] = {
            ...updatedNearbyAirport[index],
            selectedValues: selectedValues,
          }
          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              [type]: updatedNearbyAirport,
            },
          }
        })
        return
      case TRIP_DURATION:
        updateFilterData((prevState: any) => ({
          ...prevState,
          [legKey]: {
            ...prevState[legKey],
            tripDuration: {
              ...prevState[legKey]?.tripDuration,
              current: { ...prevState[legKey]?.tripDuration.current, max: selectedValues },
            },
          },
        }))
        return
      case LAYOVER_DURATION:
        updateFilterData((prevState: any) => ({
          ...prevState,
          [legKey]: {
            ...prevState[legKey],
            layoverDuration: {
              ...prevState[legKey]?.layoverDuration,
              current: selectedValues,
            },
          },
        }))
        return
      case ONE_WAY_PRICE:
        setSelectedPrice(selectedValues)
        updateFilterData((prevState: any) => ({
          ...prevState,
          [legKey]: {
            ...prevState[legKey],
            oneWayPrice: {
              ...prevState[legKey]?.oneWayPrice,
              [currentField]: selectedValues,
            },
          },
        }))
        return
      case CHECK_IN_BAGGAGE:
        updateFilterData((prevState: any) => ({
          ...prevState,
          [legKey]: {
            ...prevState[legKey],
            fareInclusions: {
              ...prevState[legKey]?.fareInclusions,
              selectedValues: selectedValues,
            },
          },
        }))
        return
      case LAYOVER_AIRPORT:
        updateFilterData((prevState: any) => {
          const availableLayoverAirport = prevState[legKey]?.layoverAirport?.availableLayoverAirport || {}
          const groupedSelectedOptions: any = {}
          selectedValues.forEach((selectedValue: string) => {
            Object.entries(availableLayoverAirport).forEach(([country, airports]: [string, any]) => {
              if (Array.isArray(airports) && airports.includes(selectedValue)) {
                if (!groupedSelectedOptions[country]) {
                  groupedSelectedOptions[country] = []
                }
                if (!groupedSelectedOptions[country].includes(selectedValue)) {
                  groupedSelectedOptions[country].push(selectedValue)
                }
              }
            })
          })

          return {
            ...prevState,
            [legKey]: {
              ...prevState[legKey],
              layoverAirport: {
                ...prevState[legKey]?.layoverAirport,
                selectedOptions: groupedSelectedOptions,
              },
            },
          }
        })
        return
      case TOTAL_ROUND_TRIP_PRICE:
        updateFilterData((prevState: any) => ({
          ...prevState,
          totalRoundTripPrice: {
            ...prevState?.totalRoundTripPrice,
            [currentField]: selectedValues,
          },
        }))
        return
    }
  }

  const sendRavenOnFilterChange = (type: PossibleFilterTypes, selectedValues: any, index = 0, legKey = 'onwardLeg') => {
    //leg wise see if need to change here as well
    try {
      let selectedFiltersList: any = []

      if (type == filterTypes.CT_UPGRADE) {
        selectedFiltersList.push(selectedValues.checked ? 'yes' : 'no')
      } else if (type == filterTypes.LAYOVER_DURATION) {
        selectedFiltersList.push(selectedValues.max)
      } else {
        selectedFiltersList.push(selectedValues)
      }

      ravenSDKSrpCfwTrigger('a_filter_applied', searchData, processedData, {
        filter_type: `${type}${legKey === 'onwardLeg' ? '_onward' : '_return'}` || 'N/A',
        filter_value: selectedFiltersList?.join(','),
        a_fare_type: processedData?.sft || 'regular',
        a_nearby_flights: '',
        a_is_filter_applied: 'yes',
      })
    } catch (e) {
      console.log(e)
    }
  }

  const stickyOffsetTop = isSrpRevamp ? SRP_REVAMP_FILTER_STICKY_TOP : 0
  const railHeight = isSrpRevamp ? `calc(100vh - ${SRP_REVAMP_FILTER_STICKY_TOP}px)` : undefined
  const railClassName = isSrpRevamp
    ? 'oy-scroll no-scrollbar flex flex-column flex-rg-3'
    : 'h-100vh oy-scroll no-scrollbar mx-4'

  // Every filter block, keyed by section. Only the revamp uses the configurable order; control keeps its own.
  const filterSections: Record<SrpFilterSection, React.ReactNode> = {
    ctUpgrade: showCtUpgrade && (
      <FilterSection isRevamp={isSrpRevamp} showDivider={false}>
        <CtUpgradeFilter isRevamp={isSrpRevamp} onFilterChange={onFilterChange} selectedFilters={selectedFilters} />
      </FilterSection>
    ),
    totalRoundTripPrice: isRT && (isIntl || isSrpRevamp) && !isEmpty(totalRoundTripPrice) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <TotalRoundTripPrice
          isRevamp={isSrpRevamp}
          currentPrice={showPerAdultPricing ? totalRoundTripPrice?.currentADT : totalRoundTripPrice?.current}
          minPrice={showPerAdultPricing ? totalRoundTripPrice?.minADT : totalRoundTripPrice?.min}
          maxPrice={showPerAdultPricing ? totalRoundTripPrice?.maxADT : totalRoundTripPrice?.max}
          onFilterChange={onFilterChange}
        />
      </FilterSection>
    ),
    ptcFare: (onwardPtcFare?.enabled || returnPtcFare?.enabled) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <PtcFareFilterContainer
          isRevamp={isSrpRevamp}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          isRT={isRT}
          onwardPtcFare={onwardPtcFare}
          processedData={processedData}
          returnPtcFare={returnPtcFare}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
        />
      </FilterSection>
    ),
    nearbyAirport: (!isEmpty(nearbyAirportFilter) || !isEmpty(returnNearbyAirportFilter)) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <NearbyAirportNew
          isRevamp={isSrpRevamp}
          nearbyAirportFilter={nearbyAirportFilter}
          returnNearbyAirportFilter={returnNearbyAirportFilter}
          onFilterChange={onFilterChange}
          isRT={isRT}
          isPerAdultPricing={showPerAdultPricing}
          processedData={processedData}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          selectedFilters={selectedFilters}
        />
      </FilterSection>
    ),
    stops: (!isEmpty(stops) || !isEmpty(returnStops)) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <StopsFilterNew
          isRevamp={isSrpRevamp}
          stops={stops}
          returnStops={returnStops}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          isPerAdultPricing={showPerAdultPricing}
          isRT={isRT}
          processedData={processedData}
        />
      </FilterSection>
    ),
    visaRejection: showVisaRejection && (
      <FilterSection isRevamp={isSrpRevamp}>
        <VisaRejectionFilter
          isRevamp={isSrpRevamp}
          onFilterChange={onFilterChange}
          selectedFilters={selectedFilters}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          isRT={isRT}
        />
      </FilterSection>
    ),
    checkInBaggage: (checkInBaggageRequired(fareInclusions) || checkInBaggageRequired(returnFareInclusions)) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <CheckInBaggageFilterNew
          isRevamp={isSrpRevamp}
          onFilterChange={onFilterChange}
          fareInclusions={fareInclusions}
          returnFareInclusions={returnFareInclusions}
          isRT={isRT}
          processedData={processedData}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          selectedFilters={selectedFilters}
        />
      </FilterSection>
    ),
    airlines: (!isEmpty(airlineFilter) || !isEmpty(returnAirlineFilter)) && (
      <FilterSection isRevamp={isSrpRevamp}>
        <AirlineFilterNew
          isRevamp={isSrpRevamp}
          onFilterChange={onFilterChange}
          airlineFilter={airlineFilter}
          showPerAdultPricing={showPerAdultPricing}
          isRT={isRT}
          returnAirlineFilter={returnAirlineFilter}
          processedData={processedData}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          selectedFilters={selectedFilters}
        />
      </FilterSection>
    ),
    timings: (
      <FilterSection isRevamp={isSrpRevamp}>
        <TimingsFilterNew
          isRevamp={isSrpRevamp}
          departureTimeFrom={departureTimeFrom}
          arrivalTimeFrom={arrivalTimeFrom}
          returnDepartureTimeFrom={returnDepartureTimeFrom}
          returnArrivalTimeFrom={returnArrivalTimeFrom}
          onFilterChange={onFilterChange}
          isRT={isRT}
          processedData={processedData}
          resetTimingsFilters={resetTimingsFilters}
          selectedFilters={selectedFilters}
        />
      </FilterSection>
    ),
    price: (
      <FilterSection isRevamp={isSrpRevamp}>
        <PriceFilterNew
          isRevamp={isSrpRevamp}
          oneWayPrice={oneWayPrice}
          returnOneWayPrice={returnOneWayPrice}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          isRT={isRT}
          showPerAdultPricing={showPerAdultPricing}
          processedData={processedData}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
        />
      </FilterSection>
    ),
    duration: (
      <FilterSection isRevamp={isSrpRevamp}>
        <DurationFilterNew
          isRevamp={isSrpRevamp}
          tripDuration={tripDuration}
          returnTripDuration={returnTripDuration}
          layoverDuration={layoverDuration}
          returnLayoverDuration={returnLayoverDuration}
          onFilterChange={onFilterChange}
          selectedFilters={selectedFilters}
          filterTypes={filterTypes}
          isRT={isRT}
          processedData={processedData}
          resetDurationFilters={resetDurationFilters}
        />
      </FilterSection>
    ),
    layoverAirport: showLayoverAirport && (
      <FilterSection isRevamp={isSrpRevamp}>
        <LayoverAirportFilterNew
          isRevamp={isSrpRevamp}
          onFilterChange={onFilterChange}
          layoverAirport={layoverAirport}
          isRT={isRT}
          returnLayoverAirport={returnLayoverAirport}
          processedData={processedData}
          clearFiltersCategoryWise={clearFiltersCategoryWise}
          selectedFilters={selectedFilters}
        />
      </FilterSection>
    ),
  }
  const revampFilterOrder = isIntl ? SRP_REVAMP_FILTER_ORDER.intl : SRP_REVAMP_FILTER_ORDER.dom
  const filterOrder = isSrpRevamp ? revampFilterOrder : SRP_FILTER_ORDER_CONTROL
  const orderedFilterSections = filterOrder.map(key => <Fragment key={key}>{filterSections[key]}</Fragment>)

  return (
    <StickyBox offsetBottom={0} offsetTop={stickyOffsetTop} style={{ zIndex: 20, alignSelf: 'flex-start' }} className='mb-10'>
      <Container
        css={{
          width: isSrpRevamp ? SRP_REVAMP_FILTER_RAIL_WIDTH : '258px',
          height: railHeight,
          overflowX: 'hidden',
          paddingBottom: `${filterBottomPadding}px`,
        }}
        className={railClassName}
      >
        <Container className='flex flex-middle my-2'>
          <Typography variant={'HM3'} className='mr-1'>
            {updatedCardCount}
          </Typography>
          <Typography variant={'B1'} color='subheading'>
            of {totalFlightsCount} flights
          </Typography>
          {showResetFilters ? (
            <Container
              onClick={() => {
                resetFilters()
              }}
              className='flex flex-1 ta-right'
            >
              <Typography variant='L3' color='link' className='w-100p c-pointer'>
                Clear all filters
              </Typography>
            </Container>
          ) : (
            ''
          )}
        </Container>

        <AppliedFiltersAllChips
          isRT={isRT}
          isIntl={isIntl}
          isRevamp={isSrpRevamp}
          totalRoundTripPrice={totalRoundTripPrice}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          filterTypes={filterTypes}
          isNonStopDefaultApplied={isNonStopDefaultApplied}
          onNonStopTooltipDismiss={onNonStopTooltipDismiss}
        />

        {orderedFilterSections}
      </Container>
    </StickyBox>
  )
}

export default withErrorBoundary(Filters, {
  boundary: 'srp-filters',
  page: 'srp',
  fallbackRender: () => <FilterErrorFallback />,
})
